import { Edit3, RotateCcw } from "lucide-react";
import type { MetaDiff } from "./logDerivation";

const THUMB_STYLE = {
  maxWidth: 120,
  maxHeight: 68,
  objectFit: "cover" as const,
};

interface DiffSideProps {
  label: string;
  isImage: boolean;
  imageSrc?: string;
  text?: string;
  imageAlt: string;
  variant: "before" | "after";
}

const DiffSide = ({
  label,
  isImage,
  imageSrc,
  text,
  imageAlt,
  variant,
}: DiffSideProps) => {
  const isBefore = variant === "before";
  return (
    <div
      className={`col-6 p-3 ${isBefore ? "border-end" : ""}`}
      style={{ backgroundColor: isBefore ? "#f2f6ff" : "#e3ecff" }}
    >
      <div
        className={`text-uppercase fw-semibold mb-1 ${
          isBefore ? "" : "text-primary"
        }`}
        style={{ fontSize: "0.6rem", ...(isBefore && { color: "#7d97c9" }) }}
      >
        {label}
      </div>
      {isImage ? (
        imageSrc ? (
          <img
            src={imageSrc}
            alt={imageAlt}
            className="rounded border"
            style={THUMB_STYLE}
          />
        ) : (
          <div className="small text-secondary">(none)</div>
        )
      ) : (
        <div className="small">{text}</div>
      )}
    </div>
  );
};

interface MetaDiffCardProps {
  diff: MetaDiff;
  reverting: boolean;
  onRevert: (diff: MetaDiff) => void;
}

/** A single before/after field diff with a per-field revert button. */
const MetaDiffCard = ({ diff, reverting, onRevert }: MetaDiffCardProps) => {
  const isImage = diff.key.startsWith("images");
  if (diff.establishing) {
    return (
      <div className="border rounded-3 overflow-hidden">
        <div
          style={{ backgroundColor: "#f6f9ff" }}
          className="d-flex align-items-center gap-2 px-3 py-1 border-bottom small fw-semibold text-uppercase text-secondary"
        >
          <Edit3 size={12} /> {diff.field}
        </div>
        <div className="p-3">
          {isImage ? (
            diff.afterImage ? (
              <img
                src={diff.afterImage}
                alt="New thumbnail"
                className="rounded border"
                style={THUMB_STYLE}
              />
            ) : (
              <div className="small text-secondary">(none)</div>
            )
          ) : (
            <div className="small">{diff.after}</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="border rounded-3 overflow-hidden">
      <div
        style={{ backgroundColor: "#f6f9ff" }}
        className="d-flex align-items-center justify-content-between px-3 py-1 border-bottom"
      >
        <span className="d-flex align-items-center gap-2 small fw-semibold text-uppercase text-secondary">
          <Edit3 size={12} /> {diff.field}
        </span>
        <button
          className="btn btn-sm btn-outline-primary rounded-pill d-flex align-items-center gap-1"
          disabled={!diff.hasBefore || reverting}
          title={
            diff.hasBefore
              ? `Revert ${diff.field} to its previous value`
              : "No previous value to revert to"
          }
          onClick={(e) => {
            e.stopPropagation();
            onRevert(diff);
          }}
        >
          {reverting ? (
            <span className="spinner-border spinner-border-sm" role="status" />
          ) : (
            <>
              <RotateCcw size={12} />
              <span style={{ fontSize: "10px" }}> Revert to previous </span>
            </>
          )}
        </button>
      </div>
      <div className="row g-0">
        <DiffSide
          variant="before"
          label="Before"
          isImage={isImage}
          imageSrc={diff.beforeImage}
          text={diff.before}
          imageAlt="Previous thumbnail"
        />
        <DiffSide
          variant="after"
          label="After"
          isImage={isImage}
          imageSrc={diff.afterImage}
          text={diff.after}
          imageAlt="New thumbnail"
        />
      </div>
    </div>
  );
};

export default MetaDiffCard;
